import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import {
  HiMenu,
  HiSearch,
  HiX,
  HiTrash,
  HiChevronLeft,
  HiChevronRight,
  HiPlus,
  HiChevronDown,
} from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import EditOrderIcon from '../../assets/icons/edit-order.svg';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { createFaq, fetchFaqList, updateFaq, deleteFaq } from '../../services/faqService';

// Module cache: instant list when returning to this tab; only first-ever load uses full-screen loader
const faqCache = {
  firstLoad: true,
  lastSearch: '',
  faqs: null,
  totalRecords: 0,
  page: 1,
};

const extractFaqsFromResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.list)) return response.list;
  return [];
};

const extractTotalFromFaqResponse = (response, itemsLength, page, pageSize) => {
  const totalRaw =
    response?.total ??
    response?.data?.total ??
    response?.data?.totalDocs ??
    response?.data?.count ??
    response?.data?.pagination?.total ??
    response?.data?.pagination?.totalRecords ??
    response?.data?.pagination?.totalCount ??
    response?.pagination?.total ??
    response?.pagination?.totalRecords ??
    response?.pagination?.totalCount ??
    response?.meta?.total ??
    response?.result?.total ??
    null;

  if (totalRaw !== null && totalRaw !== undefined && totalRaw !== '') {
    const n = Number(totalRaw);
    if (!Number.isNaN(n)) return n;
  }

  // No server total: infer so pagination can reach the next page when the first page is full.
  if (itemsLength < pageSize) {
    return (page - 1) * pageSize + itemsLength;
  }
  // Full page: assume at least one more row may exist (user can open page 2+; totals correct when a short page loads).
  return page * pageSize + 1;
};

const mapFaqRow = (item) => ({
  id: item._id || item.id || Date.now().toString(),
  question: item.question || '',
  answer: item.answer || '',
  date: item.createdAt ? new Date(item.createdAt).toLocaleString() : new Date().toLocaleString(),
  order: item.order || 0,
  is_active: item.is_active !== undefined ? item.is_active : true,
});

// Dummy FAQ data
const initialDummyData = [
  {
    id: '1',
    question: 'What is Vedify?',
    answer: 'Vedify is an educational platform designed to help students learn and improve their knowledge through interactive modules, lessons, and quizzes.',
    date: new Date().toLocaleString(),
  },
  {
    id: '2',
    question: 'How do I get started?',
    answer: 'To get started, simply create an account, choose a module that interests you, and begin with the first lesson. You can track your progress and earn karma coins as you complete lessons.',
    date: new Date().toLocaleString(),
  },
 
];

const Faq = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => faqCache.lastSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => faqCache.lastSearch || '');
  const [currentPage, setCurrentPage] = useState(() => faqCache.page || 1);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [faqData, setFaqData] = useState(() =>
    Array.isArray(faqCache.faqs) ? faqCache.faqs : []
  );
  const [totalRecords, setTotalRecords] = useState(() => faqCache.totalRecords || 0);
  const [reorderFetchLoading, setReorderFetchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(() => faqCache.firstLoad);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '' });
  const [formErrors, setFormErrors] = useState({ question: '', answer: '' });
  const [formGeneralError, setFormGeneralError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderFaqs, setReorderFaqs] = useState([]);
  const [reorderSaveLoading, setReorderSaveLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  /** Full-page loader during add / update / delete / reorder refresh. */
  const [listMutationLoading, setListMutationLoading] = useState(false);
  /** Full-screen loader while fetching a new list page (pagination only). */
  const [pageChangeLoading, setPageChangeLoading] = useState(false);

  const itemsPerPage = 16;
  const searchDebounceTimerRef = useRef(null);
  const faqListRequestIdRef = useRef(0);
  const isInitialDebounceMountRef = useRef(true);
  const isResettingOnMountRef = useRef(false);

  const getPaginationRange = () => {
    if (totalPages === 0) return [];

    const totalPageNumbers = 5;
    const totalBlocks = totalPageNumbers + 2;

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - 1, 1);
    const rightSiblingIndex = Math.min(currentPage + 1, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;
    const pages = [firstPageIndex];

    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

    if (shouldShowLeftDots) {
      pages.push('dots');
    }

    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(page);
    }

    if (shouldShowRightDots) {
      pages.push('dots');
    }

    pages.push(lastPageIndex);
    return pages;
  };

  // Server-side search + pagination; faqData is the current page only
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  /** Short page: white card hugs content instead of filling viewport height */
  const compactFaqCard = faqData.length < itemsPerPage;
  const paginatedData = faqData;
  const leftColumn = faqData.filter((_, index) => index % 2 === 0);
  const rightColumn = faqData.filter((_, index) => index % 2 === 1);

  // Debounce search query (skip first run: search + debounced are already aligned from cache)
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    if (isInitialDebounceMountRef.current) {
      isInitialDebounceMountRef.current = false;
      return undefined;
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const changePage = (getNext) => {
    setCurrentPage((prev) => {
      const next = typeof getNext === 'function' ? getNext(prev) : getNext;
      if (next === prev) return prev;
      queueMicrotask(() => setPageChangeLoading(true));
      return next;
    });
  };

  const loadFaqs = useCallback(async (searchText = '', page = 1) => {
    const requestId = ++faqListRequestIdRef.current;

    if (faqCache.firstLoad) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const response = await fetchFaqList(page, itemsPerPage, searchText);
      const faqs = extractFaqsFromResponse(response);
      const normalizedFaqs = faqs.map(mapFaqRow);
      const total = extractTotalFromFaqResponse(response, normalizedFaqs.length, page, itemsPerPage);

      if (requestId !== faqListRequestIdRef.current) return;

      setFaqData(normalizedFaqs);
      setTotalRecords(total);
      if (normalizedFaqs.length === 0 && page > 1) {
        setCurrentPage(1);
      }
      faqCache.firstLoad = false;
      faqCache.lastSearch = searchText;
      faqCache.faqs = normalizedFaqs;
      faqCache.totalRecords = total;
      faqCache.page = page;
    } catch (err) {
      if (requestId !== faqListRequestIdRef.current) return;
      const errorMsg = err.message || 'Error loading FAQs. Please try again.';
      setError(errorMsg);
      setFaqData([]);
      setTotalRecords(0);
      faqCache.firstLoad = false;
      faqCache.faqs = null;
      faqCache.totalRecords = 0;
    } finally {
      if (requestId === faqListRequestIdRef.current) {
        setIsLoading(false);
        setPageChangeLoading(false);
      }
    }
  }, [itemsPerPage]);

  // Reset only when returning after a search was used (match achievement tab behavior)
  useEffect(() => {
    const hadSearch = (faqCache.lastSearch || '').trim() !== '';
    if (!hadSearch) return;
    isResettingOnMountRef.current = true;
    faqCache.firstLoad = true;
    faqCache.lastSearch = '';
    faqCache.faqs = null;
    faqCache.totalRecords = 0;
    faqCache.page = 1;
    setIsLoading(true);
    setSearchQuery('');
    setDebouncedSearch('');
    setCurrentPage(1);
    setFaqData([]);
    setTotalRecords(0);
    (async () => {
      await loadFaqs('', 1);
      isResettingOnMountRef.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFaqs]);

  useEffect(() => {
    loadFaqs(debouncedSearch, currentPage);
  }, [debouncedSearch, currentPage, loadFaqs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.status-dropdown-container')) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  const handleEdit = (item) => {
    setFormData({ question: item.question, answer: item.answer });
    setFormErrors({ question: '', answer: '' });
    setFormGeneralError('');
    setSelectedFaq(item);
    setIsEditing(true);
    setError(null);
    setShowFormModal(true);
  };

  const handleAdd = () => {
    setFormData({ question: '', answer: '' });
    setFormErrors({ question: '', answer: '' });
    setFormGeneralError('');
    setSelectedFaq(null);
    setIsEditing(false);
    setError(null);
    setShowFormModal(true);
  };

  const handleDelete = (item) => {
    setFaqToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!faqToDelete) return;

    setDeleting(true);
    setError(null);
    try {
      const faqId = faqToDelete.id;
      await deleteFaq(faqId);

      // Close popup first, then full-page refresh loader (same flow as achievement tab)
      setShowDeleteModal(false);
      setFaqToDelete(null);
      setListMutationLoading(true);
      await loadFaqs(debouncedSearch, currentPage);
    } catch (err) {
      const errorMsg = err.message || 'Error deleting FAQ. Please try again.';
      setError(errorMsg);
    } finally {
      setDeleting(false);
      setListMutationLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setFaqToDelete(null);
  };

  const handleSave = async () => {
    // Reset form errors
    const errors = { question: '', answer: '' };
    let hasErrors = false;

    // Validate question
    if (!formData.question.trim()) {
      errors.question = 'Question is required';
      hasErrors = true;
    } else if (formData.question.trim().length < 5) {
      errors.question = 'Minimum 5 characters required';
      hasErrors = true;
    }

    // Validate answer
    if (!formData.answer.trim()) {
      errors.answer = 'Answer is required';
      hasErrors = true;
    } else if (formData.answer.trim().length < 10) {
      errors.answer = 'Minimum 10 characters required';
      hasErrors = true;
    }

    setFormErrors(errors);
    if (hasErrors) {
      return;
    }

    setSaving(true);
    setError(null);
    setFormErrors({ question: '', answer: '' });
    setFormGeneralError('');
    const wasEditing = Boolean(isEditing && selectedFaq);
    try {
      if (wasEditing) {
        const order = selectedFaq.order || 1;
        const isActive = selectedFaq.is_active !== undefined ? selectedFaq.is_active : true;
        await updateFaq(selectedFaq.id, formData.question, formData.answer, order, isActive);
      } else {
        const order = totalRecords + 1;
        await createFaq(formData.question, formData.answer, order);
      }

      // Close popup first, then full-page refresh loader (same flow as achievement tab)
      setShowFormModal(false);
      setFormData({ question: '', answer: '' });
      setFormErrors({ question: '', answer: '' });
      setFormGeneralError('');
      setSelectedFaq(null);
      setIsEditing(false);

      setListMutationLoading(true);
      if (wasEditing) {
        await loadFaqs(debouncedSearch, currentPage);
      } else {
        setCurrentPage(1);
        await loadFaqs(debouncedSearch, 1);
      }
    } catch (err) {
      // Check if it's a validation error from the API
      const errorMsg = err.message || 'Error saving FAQ. Please try again.';
      const errorLower = errorMsg.toLowerCase();
      
      // Parse validation errors - check for field-specific errors in format "field: message"
      const fieldErrors = { question: '', answer: '' };
      let hasFieldErrors = false;
      
      // Parse errors in format "0: Question must be at least 5 characters long, 1: Answer must be at least 10 characters long"
      // Or format "question: Question is required" or "Question must be..."
      
      // Check for format like "0: Question must be..." or "1: Answer must be..."
      const indexedQuestionMatch = errorMsg.match(/(?:^|\d+:\s*)(?:Question|question)[^,]*?:\s*(.+?)(?:,\s*\d+:|$)/i);
      if (indexedQuestionMatch) {
        // Replace API error message with custom message
        if (errorLower.includes('at least') && (errorLower.includes('5') || errorLower.includes('character'))) {
          fieldErrors.question = 'Minimum 5 characters required';
        } else {
          fieldErrors.question = indexedQuestionMatch[1].trim();
        }
        hasFieldErrors = true;
      }
      
      const indexedAnswerMatch = errorMsg.match(/(?:^|\d+:\s*)(?:Answer|answer)[^,]*?:\s*(.+?)(?:,\s*\d+:|$)/i);
      if (indexedAnswerMatch) {
        // Replace API error message with custom message
        if (errorLower.includes('at least') && (errorLower.includes('10') || errorLower.includes('character'))) {
          fieldErrors.answer = 'Minimum 10 characters required';
        } else {
          fieldErrors.answer = indexedAnswerMatch[1].trim();
        }
        hasFieldErrors = true;
      }
      
      // Also check for format "question: message" or "answer: message"
      if (!fieldErrors.question && errorMsg.includes('question:')) {
        const questionMatch = errorMsg.match(/question:\s*(.+?)(?:,|$)/i);
        if (questionMatch) {
          const matchText = questionMatch[1].trim().toLowerCase();
          if (matchText.includes('at least') && (matchText.includes('5') || matchText.includes('character'))) {
            fieldErrors.question = 'Minimum 5 characters required';
          } else {
            fieldErrors.question = questionMatch[1].trim();
          }
          hasFieldErrors = true;
        }
      }
      
      if (!fieldErrors.answer && errorMsg.includes('answer:')) {
        const answerMatch = errorMsg.match(/answer:\s*(.+?)(?:,|$)/i);
        if (answerMatch) {
          const matchText = answerMatch[1].trim().toLowerCase();
          if (matchText.includes('at least') && (matchText.includes('10') || matchText.includes('character'))) {
            fieldErrors.answer = 'Minimum 10 characters required';
          } else {
            fieldErrors.answer = answerMatch[1].trim();
          }
          hasFieldErrors = true;
        }
      }
      
      // If no field-specific errors found, check for general validation errors
      if (!hasFieldErrors && (errorLower.includes('validation') || errorLower.includes('required') || errorLower.includes('invalid') || errorLower.includes('at least'))) {
        // Check for question-related errors
        if (errorLower.includes('question') || (errorLower.includes('at least') && errorLower.includes('5'))) {
          if (errorLower.includes('at least') && (errorLower.includes('5') || errorLower.includes('character'))) {
            fieldErrors.question = 'Minimum 5 characters required';
          } else {
            fieldErrors.question = errorMsg;
          }
          hasFieldErrors = true;
        }
        
        // Check for answer-related errors
        if (errorLower.includes('answer') || (errorLower.includes('at least') && errorLower.includes('10'))) {
          if (errorLower.includes('at least') && (errorLower.includes('10') || errorLower.includes('character'))) {
            fieldErrors.answer = 'Minimum 10 characters required';
          } else {
            fieldErrors.answer = errorMsg;
          }
          hasFieldErrors = true;
        }
        
        // If error doesn't specify field, show in both
        if (!hasFieldErrors) {
          if (errorLower.includes('at least')) {
            if (errorLower.includes('5')) {
              fieldErrors.question = 'Minimum 5 characters required';
            }
            if (errorLower.includes('10')) {
              fieldErrors.answer = 'Minimum 10 characters required';
            }
            if (!fieldErrors.question && !fieldErrors.answer) {
              fieldErrors.question = errorMsg;
              fieldErrors.answer = errorMsg;
            }
          } else {
            fieldErrors.question = errorMsg;
            fieldErrors.answer = errorMsg;
          }
          hasFieldErrors = true;
        }
      }
      
      // Always show validation errors inline, never on main page
      // For form validation errors, always show inline below fields
      if (hasFieldErrors || errorLower.includes('validation') || errorLower.includes('required') || errorLower.includes('invalid') || errorLower.includes('must be')) {
        // If we have field-specific errors, use them; otherwise show general error in both fields
        if (!hasFieldErrors) {
          fieldErrors.question = errorMsg;
          fieldErrors.answer = errorMsg;
        }
        setFormErrors(fieldErrors);
        setFormGeneralError(''); // Clear general error when showing field errors
        setError(null); // Never show form validation errors on main page
      } else {
        // For other non-validation errors (network, server errors), show in modal only
        setFormErrors({ question: '', answer: '' });
        setFormGeneralError(errorMsg); // Show in modal, not on main page
        setError(null); // Never show form-related errors on main page
      }
    } finally {
      setSaving(false);
      setListMutationLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const toggleExpanded = (itemId) => {
    // If clicking the same item, close it. Otherwise, open the clicked item and close any other.
    setExpandedItemId((prev) => (prev === itemId ? null : itemId));
  };

  const handleStatusChange = async (faqId, newStatus) => {
    setListMutationLoading(true);
    try {
      const faq = faqData.find((f) => f.id === faqId);
      
      if (!faq) {
        alert('FAQ not found. Please refresh the page.');
        setOpenDropdownId(null);
        return;
      }

      // Update FAQ via API with the new status
      await updateFaq(
        faqId,
        faq.question,
        faq.answer,
        faq.order || 1,
        newStatus
      );

      // Close dropdown after selection
      setOpenDropdownId(null);
      
      // Reload FAQs list to reflect the change
      await loadFaqs(debouncedSearch, currentPage);
    } catch (err) {
      const errorMsg = err.message || 'Failed to update FAQ status. Please try again.';
      alert(errorMsg);
      setOpenDropdownId(null);
    } finally {
      setListMutationLoading(false);
    }
  };

  const handleOpenReorderModal = () => {
    // Do not call API on Edit Order click; use already loaded data
    setReorderFetchLoading(true);
    try {
      const normalized = [...faqData].sort((a, b) => (a.order || 0) - (b.order || 0));
      setReorderFaqs(normalized);
      setShowReorderModal(true);
    } finally {
      setReorderFetchLoading(false);
    }
  };

  const handleSaveFaqOrder = async () => {
    setReorderSaveLoading(true);
    try {
      // Update each FAQ's order via API
      const updatePromises = reorderFaqs.map(async (faq, index) => {
        const newOrder = index + 1;
        return updateFaq(
          faq.id,
          faq.question,
          faq.answer,
          newOrder,
          faq.is_active !== undefined ? faq.is_active : true
        );
      });

      await Promise.all(updatePromises);

      setShowReorderModal(false);

      setListMutationLoading(true);
      await loadFaqs(debouncedSearch, currentPage);
    } catch (err) {
      const errorMsg = err.message || 'Failed to save FAQ order. Please try again.';
      alert(errorMsg);
    } finally {
      setReorderSaveLoading(false);
      setListMutationLoading(false);
    }
  };

  // Custom Grid Icon Component (3 rows, 2 dots each - vertical layout)
  const GridIcon = ({ size = 18 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400"
    >
      {/* Left column - 3 dots */}
      <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
      {/* Right column - 3 dots */}
      <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
    </svg>
  );

  const SortableFaqItem = ({ faq, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({
      id: faq.id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
            {index + 1}
          </div>
          <p className="text-black font-medium flex-1 min-w-0 break-words line-clamp-2">
            {faq.question}
          </p>
        </div>
        <div className="flex-shrink-0 ml-2">
          <GridIcon size={18} />
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">FAQ Management</h1>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-3 md:gap-4">
              {/* Search Input */}
              <div className="w-full md:w-40">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] rounded-xl focus:outline-none focus:border-[#ECECEC] bg-white"
                  />
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex items-center justify-between md:justify-end md:gap-2 w-full md:w-auto">
                <button
                  type="button"
                  onClick={handleOpenReorderModal}
                  disabled={reorderFetchLoading}
                  className="edit-order-btn flex items-center gap-2 px-3 py-2 md:py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span>{reorderFetchLoading ? 'Loading…' : 'Edit Order'}</span>
                </button>
                <button
                  onClick={handleAdd}
                  className="flex items-center gap-2 px-3 py-2 md:py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                >
                  <HiPlus className="w-5 h-5" />
                  <span>Add FAQ</span>
                </button>
              </div>
            </div>
          </div>

          {/* FAQ List View */}
          {isLoading || listMutationLoading || pageChangeLoading ? (
            <div
              className={`fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center ${
                listMutationLoading ? 'z-[60]' : 'z-40'
              }`}
            >
              <div className="flex items-center gap-3" role="status" aria-live="polite">
                <svg
                  className="w-12 h-12 animate-spin"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#E9D5FF" />
                      <stop offset="25%" stopColor="#C084FC" />
                      <stop offset="50%" stopColor="#A855F7" />
                      <stop offset="75%" stopColor="#9333EA" />
                      <stop offset="100%" stopColor="#6B21A8" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="url(#purple-gradient)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray="60 200"
                    fill="none"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>
          ) : totalRecords === 0 && !error ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
              <div className="text-center text-gray-500 pointer-events-auto">
              <div className="mb-6">
                    <svg
                      className="w-24 h-24 mx-auto text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        No FAQ items added yet
                      </h3>
                      <p className="text-sm text-gray-500 mb-6">
                        Start by adding the first FAQ item
                      </p>
                      <button
                        onClick={handleAdd}
                        className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90"
                      >
                        <span className="text-base">+</span>
                        <span>Add FAQ</span>
                      </button>
                    </div>
                
                </div>
            
            
          ) : (
            <div
              className={`bg-white rounded-xl shadow-sm overflow-hidden flex flex-col w-full ${
                compactFaqCard ? 'flex-none' : 'flex-1 min-h-0'
              }`}
            >
              <div
                className={`faq-page-scroll w-full ${
                  compactFaqCard
                    ? 'max-h-[min(75vh,calc(100vh-14rem))] overflow-y-auto'
                    : 'min-h-0 flex-1 overflow-y-auto'
                }`}
              >
              <div className="w-full">
                {error ? (
                  <div className="px-4 py-6 text-center text-red-500">
                    {error}
                  </div>
                ) : (
                  <div className="flex gap-4 p-4">
                    {/* Left Column - Shows all items on mobile, even-indexed on desktop */}
                    <div className="flex-1 flex flex-col gap-4">
                      {/* On mobile: show all items, on desktop 2xl+: show even-indexed items */}
                      <div className="2xl:hidden flex flex-col gap-4">
                        {paginatedData.map((item) => {
                          const isExpanded = expandedItemId === item.id;
                          return (
                            <div
                              key={item.id}
                              className="faq-grid-item bg-white border border-[#ECECEC] rounded-xl hover:bg-gray-50"
                            >
                              <div className="flex flex-col">
                                {/* Mobile Layout */}
                                <div className="md:hidden px-4 py-4 flex flex-col gap-3">
                                  {/* Top row: Status dropdown on left, Edit/Delete/Chevron on right */}
                                  <div className="flex items-center justify-between gap-2">
                                    {/* Status dropdown on left */}
                                    <div
                                      className="relative status-dropdown-container flex-shrink-0 flex items-center gap-1"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {(() => {
                                        const isActive = item.is_active !== undefined ? item.is_active : true;
                                        const showDropdown = openDropdownId === item.id;
                                        return (
                                          <>
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenDropdownId(showDropdown ? null : item.id);
                                              }}
                                              className={`px-2 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 w-[90px] justify-start ${isActive
                                                ? 'bg-[#9FF7C9]/30 text-[#60BC7C]/100 hover:bg-green-600/30 '
                                                : 'bg-[#FFF0F0] text-[#F76464]  hover:bg-red-600/10'
                                                }`}
                                            >
                                              <span>{isActive ? 'Active' : 'Inactive'}</span>
                                              <HiChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ml-auto ${showDropdown ? 'rotate-180' : ''}`} />
                                            </button>
                                            {showDropdown && (
                                              <div className="absolute z-50 mt-1 left-0 w-[90px] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 top-full">
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStatusChange(item.id, !isActive);
                                                  }}
                                                  className="w-full px-2 py-1 text-xs font-semibold flex items-center justify-center rounded-lg transition-colors bg-[#ECECEC] text-black border border-[#A6A6A6] hover:bg-gray-200"
                                                >
                                                  <span>{isActive ? 'Inactive' : 'Active'}</span>
                                                </button>
                                              </div>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                    {/* Edit, Delete, and Chevron on right */}
                                    <div
                                      className="flex items-center gap-2 flex-shrink-0"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        onClick={() => handleEdit(item)}
                                        className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                        style={{ backgroundColor: '#F2E2F7' }}
                                        title="Edit"
                                      >
                                        <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                                      </button>
                                      {/* <button
                                        onClick={() => handleDelete(item)}
                                        className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-colors"
                                        title="Delete"
                                      >
                                        <MdDelete className="w-4 h-4" />
                                      </button> */}
                                      <button
                                        onClick={() => toggleExpanded(item.id)}
                                        className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
                                      >
                                        <HiChevronDown
                                          className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                        />
                                      </button>
                                    </div>
                                  </div>
                                  {/* Second row: Question */}
                                  <div
                                    className="cursor-pointer"
                                    onClick={() => toggleExpanded(item.id)}
                                  >
                                    <h3 className="text-sm font-semibold text-gray-900">
                                      {item.question}
                                    </h3>
                                  </div>
                                  {/* Third row: Answer (when expanded) */}
                                  <div 
                                    className="overflow-hidden transition-all duration-300 ease-in-out"
                                    style={{
                                      maxHeight: isExpanded ? '1000px' : '0',
                                      opacity: isExpanded ? 1 : 0,
                                    }}
                                  >
                                    <div className="pt-2 border-t border-gray-200">
                                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                        {item.answer}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* Desktop Layout */}
                                <div
                                  className="hidden md:flex px-4 py-4 items-start justify-between gap-4 cursor-pointer flex-1"
                                  onClick={() => toggleExpanded(item.id)}
                                >
                                  <div className="flex-1 flex items-start gap-3">
                                    <HiChevronDown
                                      className={`w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                    />
                                    <div className="flex-1">
                                      <h3 className="text-sm font-semibold text-gray-900">
                                        {item.question}
                                      </h3>
                                      <div 
                                        className="overflow-hidden transition-all duration-300 ease-in-out"
                                        style={{
                                          maxHeight: isExpanded ? '1000px' : '0',
                                          opacity: isExpanded ? 1 : 0,
                                        }}
                                      >
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                            {item.answer}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    className="flex items-center gap-2 flex-shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="relative status-dropdown-container flex-shrink-0 flex items-center gap-1">
                                      {(() => {
                                        const isActive = item.is_active !== undefined ? item.is_active : true;
                                        const showDropdown = openDropdownId === item.id;
                                        return (
                                          <>
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenDropdownId(showDropdown ? null : item.id);
                                              }}
                                              className={`px-2 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 w-[90px] justify-start ${isActive
                                                ? 'bg-[#9FF7C9]/30 text-[#60BC7C]/100 hover:bg-green-600/30 '
                                                : 'bg-[#FFF0F0] text-[#F76464]  hover:bg-red-600/10'
                                                }`}
                                            >
                                              <span>{isActive ? 'Active' : 'Inactive'}</span>
                                              <HiChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ml-auto ${showDropdown ? 'rotate-180' : ''}`} />
                                            </button>
                                            {showDropdown && (
                                              <div className="absolute z-50 mt-1 right-0 w-[90px] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 top-full">
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStatusChange(item.id, !isActive);
                                                  }}
                                                  className="w-full px-2 py-1 text-xs font-semibold flex items-center justify-center rounded-lg transition-colors bg-[#ECECEC] text-black border border-[#A6A6A6] hover:bg-gray-200"
                                                >
                                                  <span>{isActive ? 'Inactive' : 'Active'}</span>
                                                </button>
                                              </div>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                    <button
                                      onClick={() => handleEdit(item)}
                                      className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                      style={{ backgroundColor: '#F2E2F7' }}
                                      title="Edit"
                                    >
                                      <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                                    </button>
                                    {/* <button
                                      onClick={() => handleDelete(item)}
                                      className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-colors"
                                      title="Delete"
                                    >
                                      <MdDelete className="w-4 h-4" />
                                    </button> */}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Desktop 2xl+: Left column with even-indexed items */}
                      <div className="hidden 2xl:flex flex-col gap-4">
                        {leftColumn.map((item) => {
                        const isExpanded = expandedItemId === item.id;
                        return (
                          <div
                            key={item.id}
                            className="faq-grid-item bg-white border border-[#ECECEC] rounded-xl hover:bg-gray-50"
                          >
                          <div className="flex flex-col">
                            {/* Mobile Layout */}
                            <div className="md:hidden px-4 py-4 flex flex-col gap-3">
                              {/* Top row: Status dropdown on left, Edit/Delete/Chevron on right */}
                              <div className="flex items-center justify-between gap-2">
                                {/* Status dropdown on left */}
                                <div
                                  className="relative status-dropdown-container flex-shrink-0 flex items-center gap-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(() => {
                                    const isActive = item.is_active !== undefined ? item.is_active : true;
                                    const showDropdown = openDropdownId === item.id;
                                    return (
                                      <>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenDropdownId(showDropdown ? null : item.id);
                                          }}
                                          className={`px-2 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 w-[90px] justify-start ${isActive
                                            ? 'bg-[#9FF7C9]/30 text-[#60BC7C]/100 hover:bg-green-600/30 '
                                            : 'bg-[#FFF0F0] text-[#F76464]  hover:bg-red-600/10'
                                            }`}
                                        >
                                          <span>{isActive ? 'Active' : 'Inactive'}</span>
                                          <HiChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ml-auto ${showDropdown ? 'rotate-180' : ''}`} />
                                        </button>
                                        {showDropdown && (
                                          <div className="absolute z-50 mt-1 left-0 w-[90px] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 top-full">
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleStatusChange(item.id, !isActive);
                                              }}
                                              className="w-full px-2 py-1 text-xs font-semibold flex items-center justify-center rounded-lg transition-colors bg-[#ECECEC] text-black border border-[#A6A6A6] hover:bg-gray-200"
                                            >
                                              <span>{isActive ? 'Inactive' : 'Active'}</span>
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                {/* Edit, Delete, and Chevron on right */}
                                <div
                                  className="flex items-center gap-2 flex-shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() => handleEdit(item)}
                                    className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                    style={{ backgroundColor: '#F2E2F7' }}
                                    title="Edit"
                                  >
                                    <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                                  </button>
                                  {/* <button
                                    onClick={() => handleDelete(item)}
                                    className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-colors"
                                    title="Delete"
                                  >
                                    <MdDelete className="w-4 h-4" />
                                  </button> */}
                                  <button
                                    onClick={() => toggleExpanded(item.id)}
                                    className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
                                  >
                                    <HiChevronDown
                                      className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                    />
                                  </button>
                                </div>
                              </div>
                              {/* Second row: Question */}
                              <div
                                className="cursor-pointer"
                                onClick={() => toggleExpanded(item.id)}
                              >
                                <h3 className="text-sm font-semibold text-gray-900">
                                  {item.question}
                                </h3>
                              </div>
                              {/* Third row: Answer (when expanded) */}
                              <div 
                                className="overflow-hidden transition-all duration-300 ease-in-out"
                                style={{
                                  maxHeight: isExpanded ? '1000px' : '0',
                                  opacity: isExpanded ? 1 : 0,
                                }}
                              >
                                <div className="pt-2 border-t border-gray-200">
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                    {item.answer}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Desktop Layout */}
                            <div
                              className="hidden md:flex px-4 py-4 items-start justify-between gap-4 cursor-pointer flex-1"
                              onClick={() => toggleExpanded(item.id)}
                            >
                              <div className="flex-1 flex items-start gap-3">
                                <HiChevronDown
                                  className={`w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                />
                                <div className="flex-1">
                                  <h3 className="text-sm font-semibold text-gray-900">
                                    {item.question}
                                  </h3>
                                  <div 
                                    className="overflow-hidden transition-all duration-300 ease-in-out"
                                    style={{
                                      maxHeight: isExpanded ? '1000px' : '0',
                                      opacity: isExpanded ? 1 : 0,
                                    }}
                                  >
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                        {item.answer}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div
                                className="flex items-center gap-2 flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                 <div className="relative status-dropdown-container flex-shrink-0 flex items-center gap-1">
                                   {(() => {
                                     const isActive = item.is_active !== undefined ? item.is_active : true;
                                     const showDropdown = openDropdownId === item.id;
                                     return (
                                       <>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenDropdownId(showDropdown ? null : item.id);
                                          }}
                                          className={`px-2 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 w-[90px] justify-start ${isActive
                                            ? 'bg-[#9FF7C9]/30 text-[#60BC7C]/100 hover:bg-green-600/30 '
                                            : 'bg-[#FFF0F0] text-[#F76464]  hover:bg-red-600/10'
                                            }`}
                                        >
                                          <span>{isActive ? 'Active' : 'Inactive'}</span>
                                          <HiChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ml-auto ${showDropdown ? 'rotate-180' : ''}`} />
                                        </button>
                                         {showDropdown && (
                                           <div className="absolute z-50 mt-1 right-0 w-[90px] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 top-full">
                                             <button
                                               type="button"
                                               onClick={(e) => {
                                                 e.stopPropagation();
                                                 handleStatusChange(item.id, !isActive);
                                               }}
                                               className="w-full px-2 py-1 text-xs font-semibold flex items-center justify-center rounded-lg transition-colors bg-[#ECECEC] text-black border border-[#A6A6A6] hover:bg-gray-200"
                                             >
                                               <span>{isActive ? 'Inactive' : 'Active'}</span>
                                             </button>
                                           </div>
                                         )}
                                       </>
                                     );
                                   })()}
                                 </div>
                                <button
                                  onClick={() => handleEdit(item)}
                                  className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                  style={{ backgroundColor: '#F2E2F7' }}
                                  title="Edit"
                                >
                                  <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                                </button>
                                {/* <button
                                  onClick={() => handleDelete(item)}
                                  className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <MdDelete className="w-4 h-4" />
                                </button> */}
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                      </div>
                    </div>
                    {/* Right Column - Only visible on desktop 2xl+ */}
                    <div className="hidden 2xl:flex flex-1 flex-col gap-4">
                      {rightColumn.map((item) => {
                        const isExpanded = expandedItemId === item.id;
                        return (
                          <div
                            key={item.id}
                            className="faq-grid-item bg-white border border-[#ECECEC] rounded-xl hover:bg-gray-50"
                          >
                            <div className="flex flex-col">
                              {/* Mobile Layout */}
                              <div className="md:hidden px-4 py-4 flex flex-col gap-3">
                                {/* Top row: Status dropdown on left, Edit/Delete/Chevron on right */}
                                <div className="flex items-center justify-between gap-2">
                                  {/* Status dropdown on left */}
                                  <div
                                    className="relative status-dropdown-container flex-shrink-0 flex items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {(() => {
                                      const isActive = item.is_active !== undefined ? item.is_active : true;
                                      const showDropdown = openDropdownId === item.id;
                                      return (
                                        <>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenDropdownId(showDropdown ? null : item.id);
                                            }}
                                            className={`px-2 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 w-[90px] justify-start ${isActive
                                              ? 'bg-[#9FF7C9]/30 text-[#60BC7C]/100 hover:bg-green-600/30 '
                                              : 'bg-[#FFF0F0] text-[#F76464]  hover:bg-red-600/10'
                                              }`}
                                          >
                                            <span>{isActive ? 'Active' : 'Inactive'}</span>
                                            <HiChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ml-auto ${showDropdown ? 'rotate-180' : ''}`} />
                                          </button>
                                          {showDropdown && (
                                            <div className="absolute z-50 mt-1 left-0 w-[90px] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 top-full">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleStatusChange(item.id, !isActive);
                                                }}
                                                className="w-full px-2 py-1 text-xs font-semibold flex items-center justify-center rounded-lg transition-colors bg-[#ECECEC] text-black border border-[#A6A6A6] hover:bg-gray-200"
                                              >
                                                <span>{isActive ? 'Inactive' : 'Active'}</span>
                                              </button>
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                  {/* Edit, Delete, and Chevron on right */}
                                  <div
                                    className="flex items-center gap-2 flex-shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      onClick={() => handleEdit(item)}
                                      className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                      style={{ backgroundColor: '#F2E2F7' }}
                                      title="Edit"
                                    >
                                      <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                                    </button>
                                    {/* <button
                                      onClick={() => handleDelete(item)}
                                      className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-colors"
                                      title="Delete"
                                    >
                                      <MdDelete className="w-4 h-4" />
                                    </button> */}
                                    <button
                                      onClick={() => toggleExpanded(item.id)}
                                      className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
                                    >
                                      <HiChevronDown
                                        className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                      />
                                    </button>
                                  </div>
                                </div>
                                {/* Second row: Question */}
                                <div
                                  className="cursor-pointer"
                                  onClick={() => toggleExpanded(item.id)}
                                >
                                  <h3 className="text-sm font-semibold text-gray-900">
                                    {item.question}
                                  </h3>
                                </div>
                                {/* Third row: Answer (when expanded) */}
                                <div 
                                  className="overflow-hidden transition-all duration-300 ease-in-out"
                                  style={{
                                    maxHeight: isExpanded ? '1000px' : '0',
                                    opacity: isExpanded ? 1 : 0,
                                  }}
                                >
                                  <div className="pt-2 border-t border-gray-200">
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                      {item.answer}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Desktop Layout */}
                              <div
                                className="hidden md:flex px-4 py-4 items-start justify-between gap-4 cursor-pointer flex-1"
                                onClick={() => toggleExpanded(item.id)}
                              >
                                <div className="flex-1 flex items-start gap-3">
                                  <HiChevronDown
                                    className={`w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                  />
                                  <div className="flex-1">
                                    <h3 className="text-sm font-semibold text-gray-900">
                                      {item.question}
                                    </h3>
                                    <div 
                                      className="overflow-hidden transition-all duration-300 ease-in-out"
                                      style={{
                                        maxHeight: isExpanded ? '1000px' : '0',
                                        opacity: isExpanded ? 1 : 0,
                                      }}
                                    >
                                      <div className="mt-3 pt-3 border-t border-gray-200">
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                          {item.answer}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className="flex items-center gap-2 flex-shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="relative status-dropdown-container flex-shrink-0 flex items-center gap-1">
                                    {(() => {
                                      const isActive = item.is_active !== undefined ? item.is_active : true;
                                      const showDropdown = openDropdownId === item.id;
                                      return (
                                        <>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenDropdownId(showDropdown ? null : item.id);
                                            }}
                                            className={`px-2 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 w-[90px] justify-start ${isActive
                                              ? 'bg-[#9FF7C9]/30 text-[#60BC7C]/100 hover:bg-green-600/30 '
                                              : 'bg-[#FFF0F0] text-[#F76464]  hover:bg-red-600/10'
                                              }`}
                                          >
                                            <span>{isActive ? 'Active' : 'Inactive'}</span>
                                            <HiChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ml-auto ${showDropdown ? 'rotate-180' : ''}`} />
                                          </button>
                                          {showDropdown && (
                                            <div className="absolute z-50 mt-1 right-0 w-[90px] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 top-full">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleStatusChange(item.id, !isActive);
                                                }}
                                                className="w-full px-2 py-1 text-xs font-semibold flex items-center justify-center rounded-lg transition-colors bg-[#ECECEC] text-black border border-[#A6A6A6] hover:bg-gray-200"
                                              >
                                                <span>{isActive ? 'Inactive' : 'Active'}</span>
                                              </button>
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                  <button
                                    onClick={() => handleEdit(item)}
                                    className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                    style={{ backgroundColor: '#F2E2F7' }}
                                    title="Edit"
                                  >
                                    <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                                  </button>
                                  {/* <button
                                    onClick={() => handleDelete(item)}
                                    className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-colors"
                                    title="Delete"
                                  >
                                    <MdDelete className="w-4 h-4" />
                                  </button> */}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              </div>

            {/* Pagination */}
            {totalPages > 0 && (
              <div className="px-4 md:px-6 py-4 flex justify-end border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => changePage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((page, idx) =>
                      page === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => changePage(page)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${page === currentPage
                            ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                            : 'text-gray-900 hover:bg-gray-100'
                            }`}
                          aria-current={page === currentPage ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => changePage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Form Modal (Add/Edit) */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">
                {isEditing ? 'Edit FAQ' : 'Add FAQ'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              {formGeneralError && !formErrors.question && !formErrors.answer && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formGeneralError}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Question <span className="text-red-500">*</span>
                  {/* <span className="text-gray-500 ml-2">({formData.question.length}/5)</span> */}
                </label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, question: value });
                    if (formErrors.question) {
                      setFormErrors({ ...formErrors, question: '' });
                    }
                  }}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    formErrors.question 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-gray-300 focus:border-gray-500'
                  }`}
                  placeholder="Enter question (minimum 5 characters)"
                />
                {formErrors.question && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.question}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Answer <span className="text-red-500">*</span>
                  {/* <span className="text-gray-500 ml-2">({formData.answer.length}/10)</span> */}
                </label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, answer: value });
                    if (formErrors.answer) {
                      setFormErrors({ ...formErrors, answer: '' });
                    }
                  }}
                  className={`w-full px-3 py-1 text-sm border rounded-lg focus:outline-none resize-none ${
                    formErrors.answer 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-gray-300 focus:border-gray-500'
                  }`}
                  placeholder="Enter answer (minimum 10 characters)"
                  rows="2"
                />
                {formErrors.answer && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.answer}</p>
                )}
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowFormModal(false);
                  setFormData({ question: '', answer: '' });
                  setFormErrors({ question: '', answer: '' });
                  setFormGeneralError('');
                  setSelectedFaq(null);
                  setIsEditing(false);
                  setError(null);
                }}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditing ? 'Updating...' : 'Adding...'}
                  </>
                ) : (
                  isEditing ? 'Update FAQ' : 'Add FAQ'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-[#ffffff] w-full max-w-lg rounded-2xl md:ml-0 lg:ml-8 xl:ml-0 p-6 flex flex-col max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-black/60 text-lg font-semibold">
                Reorder FAQs
              </h2>
            </div>

            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop FAQs to reorder them. Click <b>Save Order</b> to apply.
            </p>

            {/* Drag List */}
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={() => {
                // Prevent horizontal scrolling during drag
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                // Restore scrolling after drag
                document.body.style.overflowX = '';

                const { active, over } = event;
                if (!over || active.id === over.id) return;

                setReorderFaqs((prev) => {
                  const oldIndex = prev.findIndex(
                    (f) => f.id === active.id
                  );
                  const newIndex = prev.findIndex(
                    (f) => f.id === over.id
                  );
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                // Restore scrolling if drag is cancelled
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderFaqs.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {/* Calculate height: each item is ~72px (p-4 = 16px top/bottom + content ~40px) + gap-3 (12px) = ~84px per item, 5 items = ~420px */}
                <div className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0" style={{ maxHeight: '420px' }}>
                  {reorderFaqs.map((faq, index) => (
                    <SortableFaqItem
                      key={faq.id}
                      faq={faq}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Footer */}
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowReorderModal(false)}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveFaqOrder}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaveLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && faqToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 md:p-10">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Delete FAQ</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to delete this FAQ item?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Faq;
